import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceInsights = ({ selectedAgent, timeRange }) => {
  const [activeTab, setActiveTab] = useState('corridors');

  const corridorData = [
    {
      corridor: 'US → Mexico',
      volume: '$185,000',
      transfers: 142,
      avgTicket: '$1,303',
      share: '41%',
      trend: '+15.2%',
      trendType: 'positive'
    },
    {
      corridor: 'US → India',
      volume: '$145,000',
      transfers: 89,
      avgTicket: '$1,629',
      share: '32%',
      trend: '+8.7%',
      trendType: 'positive'
    },
    {
      corridor: 'US → Philippines',
      volume: '$120,000',
      transfers: 56,
      avgTicket: '$2,143',
      share: '27%',
      trend: '+22.4%',
      trendType: 'positive'
    }
  ];

  const revenueBreakdown = [
    {
      category: 'Transfer Fees',
      amount: '$5,550',
      percentage: '60.3%',
      avgPerTransaction: '$19.35',
      color: 'bg-primary'
    },
    {
      category: 'FX Margin',
      amount: '$2,775',
      percentage: '30.2%',
      avgPerTransaction: '$9.68',
      color: 'bg-accent'
    },
    {
      category: 'Service Fees',
      amount: '$875',
      percentage: '9.5%',
      avgPerTransaction: '$3.05',
      color: 'bg-success'
    }
  ];

  const conversionMetrics = [
    { stage: 'Quote Requests', value: 425, rate: '100%', dropOff: '-' },
    { stage: 'Registration Started', value: 358, rate: '84.2%', dropOff: '15.8%' },
    { stage: 'KYC Submitted', value: 312, rate: '73.4%', dropOff: '12.9%' },
    { stage: 'First Transfer', value: 287, rate: '67.5%', dropOff: '8.0%' },
    { stage: 'Active Customer', value: 245, rate: '57.6%', dropOff: '14.6%' }
  ];

  const tabs = [
    { id: 'corridors', label: 'Corridor Distribution', icon: 'Map' },
    { id: 'revenue', label: 'Revenue Breakdown', icon: 'DollarSign' },
    { id: 'conversion', label: 'Conversion Funnel', icon: 'TrendingUp' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="BarChart3" size={20} />
          <span>Performance Insights</span>
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {selectedAgent 
            ? `Detailed analytics for ${selectedAgent?.name}`
            : 'Network-wide performance analytics'
          }
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-colors
              ${activeTab === tab?.id
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'corridors' && (
          <div className="space-y-4">
            {corridorData?.map((corridor, index) => (
              <div key={index} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Globe" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{corridor?.corridor}</h4>
                      <p className="text-sm text-muted-foreground">{corridor?.transfers} transfers</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-foreground">{corridor?.volume}</div>
                    <div className={`text-sm font-medium ${
                      corridor?.trendType === 'positive' ? 'text-success' : 'text-error'
                    }`}>
                      {corridor?.trend}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Avg Ticket</p>
                    <p className="font-medium text-foreground">{corridor?.avgTicket}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Portfolio Share</p>
                    <p className="font-medium text-foreground">{corridor?.share}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Growth</p>
                    <p className={`font-medium ${corridor?.trendType === 'positive' ? 'text-success' : 'text-error'}`}>
                      {corridor?.trend}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {revenueBreakdown?.map((item, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-3 h-3 rounded-full ${item?.color}`}></div>
                    <span className="text-sm font-medium text-muted-foreground">{item?.percentage}</span>
                  </div>
                  <h4 className="font-medium text-foreground mb-1">{item?.category}</h4>
                  <p className="text-2xl font-bold text-foreground mb-2">{item?.amount}</p>
                  <p className="text-xs text-muted-foreground">
                    {item?.avgPerTransaction} per transaction
                  </p>
                </div>
              ))}
            </div>
            <div className="border border-border rounded-lg p-4 bg-muted/20">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-foreground">Total Revenue</h4>
                  <p className="text-sm text-muted-foreground mt-1">Based on 287 completed transfers</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">$9,200</div>
                  <div className="text-sm text-success flex items-center justify-end space-x-1">
                    <Icon name="TrendingUp" size={14} />
                    <span>+12.5% vs last period</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'conversion' && (
          <div className="space-y-4">
            {conversionMetrics?.map((stage, index) => (
              <div key={index} className="relative">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">{index + 1}</span>
                  </div>
                  <div className="flex-1 border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">{stage?.stage}</h4>
                      <div className="flex items-center space-x-4">
                        <span className="text-lg font-bold text-foreground">{stage?.value}</span>
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                          parseFloat(stage?.rate) >= 70 ? 'bg-success/10 text-success' :
                          parseFloat(stage?.rate) >= 50 ? 'bg-warning/10 text-warning': 'bg-error/10 text-error'
                        }`}>
                          {stage?.rate}
                        </span>
                      </div>
                    </div>
                    {stage?.dropOff !== '-' && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="TrendingDown" size={14} />
                        <span>{stage?.dropOff} drop-off from previous stage</span>
                      </div>
                    )}
                  </div>
                </div>
                {index < conversionMetrics?.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-4 bg-border"></div>
                )}
              </div>
            ))}
            <div className="border-2 border-primary/20 rounded-lg p-4 bg-primary/5 mt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-foreground">Overall Conversion Rate</h4>
                  <p className="text-sm text-muted-foreground mt-1">From quote to active customer</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">57.6%</div>
                  <div className="text-sm text-success flex items-center justify-end space-x-1">
                    <Icon name="TrendingUp" size={14} />
                    <span>+3.2% improvement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceInsights;